import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, TrendingDown, X } from 'lucide-react';
import { useModuleAccess } from '../hooks/useModuleAccess';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyMetrics, setKeyMetrics] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Module access control
  const { isModuleEnabled, hasAnyModule } = useModuleAccess();

  useEffect(() => {
    fetchStats();
    fetchKeyMetrics();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKeyMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const [ltifr, pss, indicators, ssemData] = await Promise.all([
        axios.get(`${API}/dashboard/ltifr-trifr`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/pss`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/leading-indicators`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ssem/dashboard`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      
      setKeyMetrics({
        ltifr: ltifr.data,
        pss: pss.data,
        indicators: indicators.data,
        ssem: ssemData.data
      });
    } catch (error) {
      console.error('Failed to fetch key metrics:', error);
    }
  };

  const openMetricModal = (metric) => {
    setSelectedMetric(metric);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6" data-testid="dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.name}</p>
        </div>

        {/* Key Safety Metrics - Clickable */}
        {keyMetrics && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-[#1B5C94] mb-4">KEY SAFETY METRICS</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* PSS */}
              <button
                onClick={() => keyMetrics.pss.score !== null && openMetricModal('pss')}
                className={`bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-l-4 border-purple-500 transition-all text-left ${
                  keyMetrics.pss.score !== null ? 'hover:shadow-lg cursor-pointer' : 'cursor-default opacity-75'
                }`}
                data-testid="pss-metric-card"
              >
                <div className="text-sm text-purple-600 font-semibold mb-1">Predictive Safety Score</div>
                {keyMetrics.pss.score !== null ? (
                  <>
                    <div className="text-4xl font-bold text-purple-700">{keyMetrics.pss.score}</div>
                    <div className="text-xs text-purple-600 mt-1">{keyMetrics.pss.risk_level}</div>
                    {keyMetrics.pss.trend && (
                      <div className="flex items-center mt-2 text-xs text-purple-600">
                        {keyMetrics.pss.trend === 'Improving' ? (
                          <TrendingUp className="w-4 h-4 mr-1" />
                        ) : (
                          <TrendingDown className="w-4 h-4 mr-1" />
                        )}
                        {keyMetrics.pss.trend}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold text-purple-700">No Data</div>
                    <div className="text-xs text-purple-600 mt-1">{keyMetrics.pss.risk_level}</div>
                    <div className="text-xs text-purple-500 mt-2 italic">
                      {keyMetrics.pss.message || 'Start recording safety data'}
                    </div>
                  </>
                )}
              </button>

              {/* LTIFR */}
              <button
                onClick={() => openMetricModal('ltifr')}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-500 hover:shadow-lg transition-all text-left"
                data-testid="ltifr-metric-card"
              >
                <div className="text-sm text-blue-600 font-semibold mb-1">LTIFR (12-Month)</div>
                <div className="text-4xl font-bold text-blue-700">{keyMetrics.ltifr.ltifr_12m_rolling}</div>
                <div className="text-xs text-blue-600 mt-1">Lost Time Injury Frequency</div>
                <div className="text-xs text-blue-600 mt-2">Current: {keyMetrics.ltifr.ltifr_current}</div>
              </button>

              {/* High-Risk Hazards */}
              <button
                onClick={() => openMetricModal('hazards')}
                className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-l-4 border-orange-500 hover:shadow-lg transition-all text-left"
                data-testid="hazards-metric-card"
              >
                <div className="text-sm text-orange-600 font-semibold mb-1">High-Risk Hazards</div>
                <div className="text-4xl font-bold text-orange-700">{keyMetrics.indicators.high_risk_hazards_open}</div>
                <div className="text-xs text-orange-600 mt-1">Currently Open</div>
                <div className="text-xs text-orange-600 mt-2">Require Attention</div>
              </button>

              {/* SSEM */}
              {(() => {
                const level = keyMetrics?.ssem?.current_overall_level || 0;
                const colorScheme = {
                  0: { bg: 'from-gray-50 to-gray-100', border: 'border-gray-500', text: 'text-gray-600', textBold: 'text-gray-700' },
                  1: { bg: 'from-red-50 to-red-100', border: 'border-red-500', text: 'text-red-600', textBold: 'text-red-700' },
                  2: { bg: 'from-orange-50 to-orange-100', border: 'border-orange-500', text: 'text-orange-600', textBold: 'text-orange-700' },
                  3: { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-500', text: 'text-yellow-600', textBold: 'text-yellow-700' },
                  4: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-500', text: 'text-blue-600', textBold: 'text-blue-700' },
                  5: { bg: 'from-indigo-50 to-indigo-100', border: 'border-indigo-500', text: 'text-indigo-600', textBold: 'text-indigo-700' },
                  6: { bg: 'from-green-50 to-green-100', border: 'border-green-500', text: 'text-green-600', textBold: 'text-green-700' }
                };
                const colors = colorScheme[level];
                
                return (
                  <button
                    onClick={() => openMetricModal('ssem')}
                    className={`bg-gradient-to-br ${colors.bg} rounded-lg p-6 border-l-4 ${colors.border} hover:shadow-lg transition-all text-left`}
                    data-testid="ssem-metric-card"
                  >
                    <div className={`text-sm ${colors.text} font-semibold mb-1`}>SSEM Maturity</div>
                    <div className={`text-4xl font-bold ${colors.textBold}`}>
                      {level > 0 ? `Level ${level}` : 'Not Assessed'}
                    </div>
                    <div className={`text-xs ${colors.text} mt-1`}>Safety Excellence Model</div>
                    <div className={`text-xs ${colors.text} mt-2`}>
                      {level === 1 && 'Reactive Level'}
                      {level === 2 && 'Managed Level'}
                      {level === 3 && 'Systematic Level'}
                      {level === 4 && 'Proactive Level'}
                      {level === 5 && 'Resilient Level'}
                      {level === 6 && 'Transformational Level'}
                      {level === 0 && 'Complete assessment to see level'}
                    </div>
                  </button>
                );
              })()}
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">Click any metric for detailed insights</p>
          </div>
        )}

        {/* Incident Snapshot */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-[#1B5C94] mb-4">INCIDENT SNAPSHOT</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-l-4 border-green-500" data-testid="open-hazards-card">
              <div className="text-3xl font-bold text-green-700">{stats?.open_hazards || 0}</div>
              <div className="text-green-600 font-semibold mt-1">Open Incidents</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-l-4 border-blue-500" data-testid="closed-hazards-card">
              <div className="text-3xl font-bold text-blue-700">{stats?.closed_hazards || 0}</div>
              <div className="text-blue-600 font-semibold mt-1">Closed Incidents</div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border-l-4 border-red-500" data-testid="overdue-actions-card">
              <div className="text-3xl font-bold text-red-700">{stats?.overdue_actions || 0}</div>
              <div className="text-red-600 font-semibold mt-1">Overdue Actions</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-[#1B5C94] mb-4">QUICK ACTIONS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Report New Incident - Requires safety or incidents module */}
            {hasAnyModule('safety', 'incidents') && (
              <button
                data-testid="report-hazard-button"
                onClick={() => navigate('/report')}
                className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
              >
                + Report New Incident
              </button>
            )}

            {/* View Incidents List - Requires safety or incidents module */}
            {hasAnyModule('safety', 'incidents') && (
              <button
                data-testid="view-hazards-button"
                onClick={() => navigate('/hazards')}
                className="bg-gradient-to-r from-[#DAA520] to-[#C47723] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
              >
                View Incidents List
              </button>
            )}

            {/* Safety Modules - Requires safety module */}
            {isModuleEnabled('safety') && (
              <button
                onClick={() => navigate('/safety')}
                className="bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
                data-testid="safety-hub-button"
              >
                🛡️ Safety Modules
              </button>
            )}

            {/* Executive Dashboard - Requires safety module */}
            {isModuleEnabled('safety') && (
              <button
                onClick={() => navigate('/dashboard/executive')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
                data-testid="executive-dashboard-quick-btn"
              >
                📊 Executive Dashboard
              </button>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Severity */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Incidents by Severity</h3>
            <div className="space-y-3">
              {Object.entries(stats?.by_severity || {}).map(([severity, count]) => (
                <div key={severity} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-4 h-4 rounded-full ${
                        severity === 'High'
                          ? 'bg-red-500'
                          : severity === 'Medium'
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                      }`}
                    ></div>
                    <span className="font-medium text-gray-700">{severity}</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-800">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Status */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Incidents by Status</h3>
            <div className="space-y-3">
              {Object.entries(stats?.by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">{status}</span>
                  <span className="text-2xl font-bold text-gray-800">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* By Type */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Incidents by Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(stats?.by_type || {}).map(([type, count]) => (
              <div key={type} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="text-xl font-bold text-[#1B5C94]">{count}</div>
                <div className="text-gray-600 text-sm mt-1">{type}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Incidents */}
        {stats?.recent_hazards?.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Incidents</h3>
            <div className="space-y-3">
              {stats.recent_hazards.map((hazard) => (
                <div
                  key={hazard.id}
                  onClick={() => navigate(`/hazards/${hazard.id}`)}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  data-testid={`recent-hazard-${hazard.id}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">{hazard.title || 'Untitled Incident'}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {hazard.date_reported ? new Date(hazard.date_reported).toLocaleDateString() : 'Date not available'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        hazard.severity === 'High'
                          ? 'bg-red-100 text-red-700'
                          : hazard.severity === 'Medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {hazard.severity}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                      {hazard.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metric Detail Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1B5C94]">
                {selectedMetric === 'pss' && 'Predictive Safety Score (PSS)'}
                {selectedMetric === 'ltifr' && 'LTIFR - Lost Time Injury Frequency Rate'}
                {selectedMetric === 'hazards' && 'High-Risk Hazards'}
                {selectedMetric === 'ssem' && 'SSEM Maturity Level'}
              </DialogTitle>
            </DialogHeader>
            
            {keyMetrics && selectedMetric === 'pss' && (
              <div className="space-y-4">
                <div className="bg-purple-50 p-6 rounded-lg border-2 border-purple-200">
                  <div className="text-center mb-4">
                    <div className="text-5xl font-bold text-purple-700">{keyMetrics.pss.score}/100</div>
                    <div className="text-lg text-purple-600 mt-2">{keyMetrics.pss.risk_level}</div>
                  </div>
                  <div className="flex items-center justify-center mt-3">
                    {keyMetrics.pss.trend === 'Improving' ? (
                      <div className="flex items-center text-green-600">
                        <TrendingUp className="w-5 h-5 mr-2" />
                        <span className="font-semibold">{keyMetrics.pss.trend} Trend</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-orange-600">
                        <TrendingDown className="w-5 h-5 mr-2" />
                        <span className="font-semibold">{keyMetrics.pss.trend} Trend</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Contributing Factors:</h4>
                  <div className="space-y-2">
                    {Object.entries(keyMetrics.pss.contributors).map(([factor, score]) => (
                      <div key={factor} className="flex items-center">
                        <span className="text-sm text-gray-600 w-48 capitalize">{factor.replace('_', ' ')}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-3 mr-3">
                          <div 
                            className="bg-purple-500 h-3 rounded-full" 
                            style={{ width: `${score}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 w-12 text-right">{Math.round(score)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What is PSS?</strong> The Predictive Safety Score combines multiple safety metrics 
                    to provide an overall safety performance indicator. Higher scores indicate better safety performance.
                  </p>
                </div>
              </div>
            )}

            {keyMetrics && selectedMetric === 'ltifr' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-6 rounded-lg text-center">
                    <div className="text-sm text-blue-600 mb-1">Current Month</div>
                    <div className="text-4xl font-bold text-blue-700">{keyMetrics.ltifr.ltifr_current}</div>
                  </div>
                  <div className="bg-blue-50 p-6 rounded-lg text-center">
                    <div className="text-sm text-blue-600 mb-1">12-Month Rolling</div>
                    <div className="text-4xl font-bold text-blue-700">{keyMetrics.ltifr.ltifr_12m_rolling}</div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Monthly Trend (Last 12 Months):</h4>
                  <div className="space-y-2">
                    {keyMetrics.ltifr.monthly_trend.slice(-6).map((month, idx) => (
                      <div key={idx} className="flex items-center">
                        <span className="text-sm text-gray-600 w-32">{month.month}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-3 mr-3">
                          <div 
                            className="bg-blue-500 h-3 rounded-full" 
                            style={{ width: `${Math.min(month.ltifr * 10, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 w-12 text-right">{month.ltifr}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {keyMetrics.ltifr.trifr_12m_rolling && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-700 mb-2">
                      <strong>TRIFR (12-Month):</strong> {keyMetrics.ltifr.trifr_12m_rolling}
                    </div>
                    <p className="text-xs text-gray-600">
                      Total Recordable Injury Frequency Rate (includes LTI, Medical Treatment, Restricted Work)
                    </p>
                  </div>
                )}
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What is LTIFR?</strong> Lost Time Injury Frequency Rate measures the number of lost time injuries 
                    per million hours worked. Lower is better. Industry benchmark: &lt;3.0
                  </p>
                </div>
              </div>
            )}

            {keyMetrics && selectedMetric === 'hazards' && (
              <div className="space-y-4">
                <div className="bg-orange-50 p-6 rounded-lg text-center border-2 border-orange-200">
                  <div className="text-5xl font-bold text-orange-700">{keyMetrics.indicators.high_risk_hazards_open}</div>
                  <div className="text-lg text-orange-600 mt-2">High-Risk Hazards Currently Open</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">High-Risk Observations</div>
                    <div className="text-3xl font-bold text-gray-800">{keyMetrics.indicators.high_risk_observations}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Unsafe Acts (30d)</div>
                    <div className="text-3xl font-bold text-gray-800">{keyMetrics.indicators.unsafe_acts_reported}</div>
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Action Required:</strong> High-risk hazards require immediate attention and mitigation. 
                    Review each hazard and ensure CAPAs are in place.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setShowModal(false);
                    navigate('/hazards');
                  }}
                  className="w-full bg-orange-600 text-white font-bold py-3 rounded-lg hover:bg-orange-700"
                >
                  View All Hazards
                </button>
              </div>
            )}

            {selectedMetric === 'ssem' && (
              <div className="space-y-4">
                {keyMetrics?.ssem ? (
                  <>
                    {(() => {
                      const level = keyMetrics.ssem.current_overall_level || 0;
                      const modalColorScheme = {
                        0: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', textLight: 'text-gray-600' },
                        1: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', textLight: 'text-red-600' },
                        2: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', textLight: 'text-orange-600' },
                        3: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', textLight: 'text-yellow-600' },
                        4: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', textLight: 'text-blue-600' },
                        5: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', textLight: 'text-indigo-600' },
                        6: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', textLight: 'text-green-600' }
                      };
                      const modalColors = modalColorScheme[level];
                      
                      return (
                        <div className={`${modalColors.bg} p-6 rounded-lg text-center border-2 ${modalColors.border}`}>
                          <div className={`text-5xl font-bold ${modalColors.text}`}>
                            Level {level}
                          </div>
                          <div className={`text-lg ${modalColors.textLight} mt-2`}>
                            {level === 1 && 'Reactive Approach'}
                            {level === 2 && 'Managed Approach'}
                            {level === 3 && 'Systematic Approach'}
                            {level === 4 && 'Proactive Approach'}
                            {level === 5 && 'Resilient Approach'}
                            {level === 6 && 'Transformational Approach'}
                            {level === 0 && 'Not Assessed Yet'}
                          </div>
                        </div>
                      );
                    })()}

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">SSEM Dimensions:</h4>
                      <div className="space-y-2">
                        {keyMetrics.ssem.dimension_breakdown && keyMetrics.ssem.dimension_breakdown.map((dim) => {
                          const barColorScheme = {
                            1: 'bg-red-500',
                            2: 'bg-orange-500',
                            3: 'bg-yellow-500',
                            4: 'bg-blue-500',
                            5: 'bg-indigo-500',
                            6: 'bg-green-500'
                          };
                          
                          return (
                            <div key={dim.dimension} className="flex items-center">
                              <span className="text-sm text-gray-600 w-32">{dim.dimension}</span>
                              <div className="flex-1 flex gap-1 mr-3">
                                {[1, 2, 3, 4, 5, 6].map((l) => (
                                  <div
                                    key={l}
                                    className={`h-6 flex-1 rounded ${
                                      l <= dim.level ? barColorScheme[l] : 'bg-gray-200'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-semibold text-gray-800 w-12 text-right">L{dim.level}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {(() => {
                      const level = keyMetrics.ssem.current_overall_level || 0;
                      const infoBoxColors = {
                        0: 'bg-gray-50',
                        1: 'bg-red-50',
                        2: 'bg-orange-50',
                        3: 'bg-yellow-50',
                        4: 'bg-blue-50',
                        5: 'bg-indigo-50',
                        6: 'bg-green-50'
                      };
                      
                      return (
                        <div className={`${infoBoxColors[level]} p-4 rounded-lg`}>
                          <p className="text-sm text-gray-700">
                            <strong>What is SSEM?</strong> Safety & Sustainability Excellence Model measures organizational maturity 
                            across 8 dimensions on a 6-level scale. Level {level} indicates {
                              level === 1 ? 'a reactive, incident-driven approach.' :
                              level === 2 ? 'a managed, compliance-focused approach.' :
                              level === 3 ? 'a systematic, documented approach to safety.' :
                              level === 4 ? 'a proactive, predictive approach to safety.' :
                              level === 5 ? 'a resilient, adaptive safety culture.' :
                              level === 6 ? 'a transformational, industry-leading approach.' :
                              'that no assessment has been completed yet.'
                            }
                          </p>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No SSEM assessment data available.</p>
                    <Button onClick={() => navigate('/ssem')} className="mt-4">
                      Complete SSEM Assessment
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;